import React, { SetStateAction } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { GoDotFill } from 'react-icons/go';
import { FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { CustomImageHandler } from '../../../../../../shared/components/CustomImageHandler';
import {
    formatDate,
    getCorrectFilterTitle,
    getSocialIcons,
    truncateString,
} from '../../../../../../shared/functions/Functions';
import { iManageFilters, iManageList } from '../dto/IManageCalendar';
import { Pagination } from '../../../../../../shared/components/Pagination';
import { CalendarListRowsSkeleton } from './CalendarListViewSkeleton';
interface iCalendarListViewProps {
    listOfInfluencers: iManageList[] | undefined;
    setFilterParams: React.Dispatch<SetStateAction<iManageFilters>>;
    filterParams: iManageFilters;
    setShowUpdateModal?: React.Dispatch<SetStateAction<boolean>>;
    setIsProposalOpened: React.Dispatch<SetStateAction<boolean>>;
    setSelectedEventId?: React.Dispatch<SetStateAction<number>>;
    isLoading?: boolean;
}

export const CalendarListView = ({
    listOfInfluencers,
    filterParams,
    setFilterParams,
    setIsProposalOpened,
    setSelectedEventId,
    setShowUpdateModal,
    isLoading = false,
}: iCalendarListViewProps) => {
    const { t } = useTranslation();
    const filterTableView = (
        sortType: 'ASC' | 'DESC',
        sortCriteria: 'displayName' | 'postingDate' | 'socialPlatformId'
    ) => {
        setFilterParams(prev => ({
            ...prev,
            sortType,
            sortCriteria,
        }));
    };

    const handleEventClick = (offerItemId: number, eventStatus: string) => {
        if (setSelectedEventId) {
            setSelectedEventId(offerItemId);
        }
        if (eventStatus === 'proposed') {
            setIsProposalOpened(true);
        } else {
            setShowUpdateModal && setShowUpdateModal(true);
        }
    };

    return (
        <div>
            <Table responsive className="table-vertical-center">
                <thead>
                    <tr>
                        <th
                            className="cursor-pointer"
                            onClick={() =>
                                filterTableView(`${filterParams?.sortType === 'DESC' ? 'ASC' : 'DESC'}`, 'displayName')
                            }
                        >
                            {t('brand.campaign.dashboard.manage.influencer')}{' '}
                            {filterParams?.sortType === 'DESC' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                        </th>
                        <th
                            className="cursor-pointer text-center"
                            onClick={() =>
                                filterTableView(`${filterParams?.sortType === 'DESC' ? 'ASC' : 'DESC'}`, 'postingDate')
                            }
                        >
                            {t('brand.campaign.dashboard.manage.posting_date')}{' '}
                            {filterParams?.sortType === 'DESC' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                        </th>
                        <th
                            className="text-center cursor-pointer"
                            onClick={() =>
                                filterTableView(
                                    `${filterParams?.sortType === 'DESC' ? 'ASC' : 'DESC'}`,
                                    'socialPlatformId'
                                )
                            }
                        >
                            {t('brand.campaign.dashboard.manage.platform')}{' '}
                            {filterParams?.sortType === 'DESC' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                        </th>
                        <th className="cursor-pointer text-center">
                            {t('brand.campaign.dashboard.manage.approved_by')}
                        </th>
                        <th className="text-center cursor-pointer">
                            {t('brand.campaign.dashboard.manage.approved_date')}
                        </th>
                        <th className="text-center cursor-pointer">{t('brand.campaign.dashboard.manage.status')}</th>
                    </tr>
                </thead>
                {isLoading ? (
                    <CalendarListRowsSkeleton />
                ) : (
                    <tbody>
                        {listOfInfluencers?.map((influencer: iManageList) => {
                            const hasNotification = !!influencer?.eventNotifications?.[0]?.isUnread;
                            const isLongTerm = influencer?.offerItem?.offer?.isLongTerm;
                            const isOnTop = influencer?.offerItem?.offer?.isOnTop;
                            const isAddonsOnly = influencer?.offerItem?.offer?.isAddonsOnly;
                            return (
                                <tr
                                    className="cursor-pointer hover-effect"
                                    key={influencer?.id}
                                    onClick={() => handleEventClick(influencer?.id, influencer?.status)}
                                >
                                    <td className="text-nowrap">
                                        <div className="d-flex align-items-center">
                                            <CustomImageHandler
                                                altTag="User Profile"
                                                classes="middle-rounded-logo mr-2"
                                                thumbnailPath={
                                                    influencer?.offerItem?.offer?.user?.profilePhoto?.thumbnail
                                                }
                                                photoPath={
                                                    influencer?.offerItem?.offer?.user?.profilePhoto?.thumbnail ?? null
                                                }
                                                name={influencer?.offerItem?.offer?.user?.displayName}
                                            />
                                            <div className="d-flex align-items-start flex-column">
                                                <span className="text-dark font-weight-semi-bold ">
                                                    {truncateString(
                                                        influencer?.offerItem?.offer?.user?.displayName ?? '',
                                                        23
                                                    )}

                                                    {hasNotification && <GoDotFill size={15} className="text-danger" />}
                                                </span>
                                                {isLongTerm && (
                                                    <div className={'custom-badge long-term'}>
                                                        {t(`brand.campaign.briefing.overview_card.long_term`)}
                                                    </div>
                                                )}
                                                {isOnTop && (
                                                    <div className={'custom-badge on-top'}>
                                                        {t(`brand.campaign.briefing.overview_card.on_top`)}
                                                    </div>
                                                )}
                                                {isAddonsOnly && (
                                                    <div className={'custom-badge additional ml-1'}>
                                                        {t('influencer.offer_management.modal.additional')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-center text-nowrap">{formatDate(influencer?.postingDate)}</td>
                                    <td className="text-center">
                                        {getSocialIcons({
                                            iconPath: influencer?.offerItem?.socialPlatform?.path,
                                            dataDown:
                                                t(`general.platforms.${influencer?.offerItem?.socialPlatform?.name}`) +
                                                ` ${influencer.platformPosition ?? '-'}/${
                                                    influencer?.offerItem?.nbPosts
                                                }`,
                                            parentClasses: 'w-100 justify-content-center',
                                        })}
                                    </td>
                                    <td>
                                        <div
                                            className={`d-flex align-items-center justify-content-${
                                                !!influencer.approvedBy ? 'start' : 'center'
                                            }`}
                                        >
                                            {influencer.approvedBy ? (
                                                <>
                                                    <CustomImageHandler
                                                        altTag="User Profile"
                                                        classes="middle-rounded-logo mr-2"
                                                        thumbnailPath={influencer?.approvedBy?.profilePhoto?.thumbnail}
                                                        photoPath={
                                                            influencer?.approvedBy?.profilePhoto?.path ||
                                                            influencer?.approvedBy?.profilePhoto?.thumbnail ||
                                                            null
                                                        }
                                                        name={
                                                            influencer?.approvedBy?.firstName?.trim() ||
                                                            influencer?.approvedBy?.displayName
                                                        }
                                                    />

                                                    <span className="mt-1 text-dark font-weight-semi-bold d-flex align-items-center">
                                                        {truncateString(
                                                            influencer?.approvedBy?.firstName?.trim() ||
                                                                influencer?.approvedBy?.displayName ||
                                                                '',
                                                            13
                                                        )}

                                                        {hasNotification && (
                                                            <GoDotFill size={15} className="text-danger" />
                                                        )}
                                                    </span>
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-center text-nowrap">
                                        {influencer?.approvedDate ? formatDate(influencer?.approvedDate) : '-'}
                                    </td>
                                    <td className="text-center">
                                        <span className={`status ${influencer?.status}`}>
                                            {getCorrectFilterTitle(influencer?.status, t)}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                )}
            </Table>

            {!isLoading && filterParams?.totalPages > 1 && (
                <Pagination
                    page={filterParams.page}
                    totalPages={filterParams?.totalPages ?? 0}
                    handlePagination={updatePage => setFilterParams({ ...filterParams, page: updatePage })}
                />
            )}
        </div>
    );
};
